import { useCallback, useMemo, useReducer } from 'react';
import fetchResults from '~/core/api/fetchResults';
import getUpdateRequestParams from '~/core/api/getUpdateRequestParams';
import { ResponseData } from '~/core/api/ResponseTypes';
import { Configuration } from '~/core/configuration/configuration.type';
import { useEvents } from '~/core/events/eventsContext';
import { CancelAllFiltersPayload, CancelFilterPayload, ResetFilterControlsPayload } from '~/core/state/actions';
import reducer from '~/core/state/reducer/reducer';
import { State } from '~/core/state/types/state';
import { useRenderingInfo } from '~/hooks/useRenderingInfo';
import SRError from '~/utils/errors/srError';
import scrollToElement from '~/utils/scrollToElement';
import useInitialRender from '~/utils/useInitialRender';
import { useControlsChange } from './effects/useControlsChange';
import { useResultsChange } from './effects/useResultsChange';
import { useSearchQueryChange } from './effects/useSearchQueryChange';
import { UpdateFilterPayload_ControlsState } from './features/filtering/types/filters.type';
import { UpdateIndexFilterPayload_ControlsState } from './features/indexFiltering/types/indices.type';
import { SelectedSort } from './features/sorting/types/sorting.type';
import { usePersistence } from './persistence/PersistenceContext';
import { createInitialState } from './state/createInitialState';
import { isAbortError, useAbortController } from './useAbortController';
import { useReset } from './useReset';
import { ResultLayoutOptions } from './features/resultLayout/types/resultLayout.type';

export interface LoadMoreParams {
    querySetIdentifier: string;
    from: number;
}
export type LoadMore = (params: LoadMoreParams) => void;

export type UpdateIndexFilterControls = (params: UpdateIndexFilterPayload_ControlsState) => void;

/**
 * Update filters controls callback
 * querySetIdenifier and filterIdentifier are curried
 */
export type UpdateFilterControlsParams = Omit<
    UpdateFilterPayload_ControlsState,
    'querySetIdentifier' | 'filterIdentifier'
>; // querySetIdentifier and filterIdentifier are curried

export type UpdateFilterControls = (params: UpdateFilterControlsParams) => void;

export type UpdateFilterControlsCurried = (
    querySetIdentifier: string
) => (filterIdentifier: string) => UpdateFilterControls;

export type ResetFilterControlsCurried = (querySetIdentifier: string) => (filterIdentifier: string) => () => void;

// cancel filters callback
export type CancelFilter = (params: CancelFilterPayload) => void;

export type CancelAllFilters = (params: CancelAllFiltersPayload) => void;

export type UpdateSearchQuery = (searchQuery: string) => void;

export interface SortResultsParams {
    querySetIdentifier: string;
    selectedSort: SelectedSort;
}

export type SortResults = (params: SortResultsParams) => void;

export type SetResultLayoutParams = ResultLayoutOptions;

export type SetResultLayout = (layout: SetResultLayoutParams) => void;

export interface UseSearchStateParams {
    config: Configuration;
    isDebugMode: boolean;
    /** Ref to the products title element */
    productsTitleRef?: React.RefObject<HTMLDivElement>;
}

export interface UseSearchStateAPI {
    state: State;
    resetState: () => void;
    updateSearchQuery: (searchQuery: string) => void;
    loadMore: LoadMore;
    updateIndexFilterControls: UpdateIndexFilterControls;
    resetFilterControls: ResetFilterControlsCurried;
    updateFilterControls: UpdateFilterControlsCurried;
    cancelFilter: CancelFilter;
    cancelAllFilters: CancelAllFilters;
    sortResults: SortResults;
    setInstantSearch: (instantSearch: boolean) => void;
    setResultLayout: SetResultLayout;
}

/**
 * Search state hook - core hook, which includes all necesarry logic for state management and provides it to the App
 * component, which subsequently distributes it to the rest of the application.
 *
 * @returns Search state hook api
 */
export const useSearchState = ({ config, isDebugMode, productsTitleRef }: UseSearchStateParams): UseSearchStateAPI => {
    const {
        eventFactory: {
            createErrorEvent,
            createSearchEvent,
            createIndicesFilterEvent,
            createFilterEvent,
            createCancelFilterEvent,
            createCancelAllFiltersEvent,
            createResetQueryEvent,
        },
        eventEmitter: { emitEvent },
    } = useEvents();
    const isInitialRender = useInitialRender();
    const { isMobileVersion } = useRenderingInfo();
    const Persistence = usePersistence();
    const initialState = useMemo(() => {
        const { searchQuery, snapshotSets, pagination } = Persistence.getPersistentState();
        return createInitialState({ config, searchQuery, snapshotSets, initialPagination: pagination });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [config]);
    const [state, dispatch] = useReducer(reducer(config, isDebugMode), initialState);
    const { abortController, resetAbortController } = useAbortController();
    const { setIsAfterReset, isAfterReset, resetState } = useReset({ dispatch, resetAbortController });

    const searchSuccess = (results: ResponseData) => {
        dispatch({
            type: 'search-success',
            payload: results,
        });
    };

    const updateResults = (results: ResponseData) => {
        dispatch({
            type: 'update-results',
            payload: results,
        });
    };

    const updateError = useCallback(
        (error: SRError) => {
            // abort error is expected when we reset the state
            if (isAbortError(error.originalError)) {
                return;
            }
            dispatch({
                type: 'update-error',
                payload: {
                    error,
                },
            });

            // Log update-error to Sentry
            // Sentry.captureException(error);

            emitEvent(createErrorEvent({ searchQuery: state.searchQuery, error }));
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [state.searchQuery]
    );

    const updateSearchQuery = useCallback((searchQuery: string) => {
        dispatch({
            type: 'change-search-query',
            payload: {
                searchQuery,
            },
        });
    }, []);

    const updateIndexFilterControls = useCallback(
        (params: UpdateIndexFilterPayload_ControlsState) => {
            dispatch({
                type: 'update-index-filter-controls',
                payload: params,
            });
            emitEvent(createIndicesFilterEvent({ searchQuery: state.searchQuery, actionPayload: params }));
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [state.searchQuery]
    );

    const updateFilterControls = useCallback(
        (querySetIdentifier: string) => (filterIdentifier: string) => (params: UpdateFilterControlsParams) => {
            //if not on mobile, scroll to the product title everytime filter is updated; don't scroll if the products title element is already in view
            if (!isMobileVersion && productsTitleRef && productsTitleRef.current) {
                scrollToElement(productsTitleRef.current);
            }

            const payload: UpdateFilterPayload_ControlsState = {
                ...params,
                querySetIdentifier,
                filterIdentifier,
            };
            dispatch({
                type: 'update-filter-controls',
                payload,
            });
            emitEvent(
                createFilterEvent({
                    searchQuery: state.searchQuery,
                    actionPayload: payload,
                })
            );
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [state.searchQuery]
    );

    const resetFilterControls = (querySetIdentifier: string) => (filterIdentifier: string) => () => {
        const payload: ResetFilterControlsPayload = {
            querySetIdentifier,
            filterIdentifier,
        };

        dispatch({
            type: 'reset-filter-controls',
            payload: payload,
        });
        // TODO: Emit event
    };

    const cancelFilter = useCallback(
        (params: CancelFilterPayload) => {
            //if not on mobile, scroll to the product title everytime filter is cancelled; don't scroll if the products title element is already in view
            if (!isMobileVersion && productsTitleRef && productsTitleRef.current) {
                scrollToElement(productsTitleRef.current);
            }

            dispatch({
                type: 'cancel-filter',
                payload: params,
            });
            emitEvent(createCancelFilterEvent({ searchQuery: state.searchQuery, actionPayload: params }));
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [state.searchQuery]
    );

    const cancelAllFilters = useCallback(
        (params: CancelAllFiltersPayload) => {
            //if not on mobile, scroll to the product title everytime all filters are cancelled; don't scroll if the products title element is already in view
            if (!isMobileVersion && productsTitleRef && productsTitleRef.current) {
                scrollToElement(productsTitleRef.current);
            }

            dispatch({
                type: 'cancel-all-filters',
                payload: params,
            });
            emitEvent(createCancelAllFiltersEvent({ searchQuery: state.searchQuery, actionPayload: params }));
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [state.searchQuery]
    );

    const loadMore = useCallback(
        ({ querySetIdentifier, from }: LoadMoreParams) => {
            (async () => {
                dispatch({
                    type: 'start-loading',
                });

                await fetchResults({
                    apiUrl: config.apiUrl,
                    searchQuery: state.searchQuery,
                    parameters: getUpdateRequestParams({
                        stateSets: state.sets,
                        stateControls: state.controls,
                        querySetIdentifier,
                        from,
                    }),
                    successCallback: ({ data, requestParams }) => {
                        const payload = {
                            querySetIdentifier: requestParams[0].name,
                            size: requestParams[0].from + requestParams[0].size,
                        };
                        Persistence.updateStoragePagination(payload);

                        dispatch({ type: 'update-categories-pagination', payload });
                        updateResults(data);
                    },
                    failedCallback: updateError,
                    abortController,
                    isDebugMode,
                });
            })();
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [config.apiUrl, isDebugMode, state.searchQuery, state.sets]
    );

    const sortResults = useCallback((params: SortResultsParams) => {
        dispatch({
            type: 'sort-results',
            payload: params,
        });
    }, []);

    const setInstantSearch = useCallback((instantSearch: boolean) => {
        dispatch({
            type: 'set-is-instant-search',
            payload: {
                instantSearch,
            },
        });
    }, []);

    const setResultLayout = useCallback((layout: SetResultLayoutParams) => {
        dispatch({
            type: 'set-result-layout',
            payload: {
                layout,
            }
        })
    }, [])

    useSearchQueryChange({
        config,
        state,
        dispatch,
        isInitialRender,
        isAfterReset,
        setIsAfterReset,
        resetState,
        searchSuccess,
        Persistence,
        emitEvent,
        createSearchEvent,
        updateError,
        isDebugMode,
        createResetQueryEvent,
        abortController,
    });

    useControlsChange({
        config,
        state,
        dispatch,
        isInitialRender,
        isAfterReset,
        setIsAfterReset,
        resetState,
        searchSuccess,
        Persistence,
        emitEvent,
        createSearchEvent,
        updateError,
        isDebugMode,
        createResetQueryEvent,
        abortController,
    });

    useResultsChange({ state, isDebugMode });

    return {
        state,
        updateSearchQuery,
        loadMore,
        updateIndexFilterControls,
        resetFilterControls,
        updateFilterControls,
        cancelFilter,
        cancelAllFilters,
        sortResults,
        resetState,
        setInstantSearch,
        setResultLayout,
    };
};
